import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Image, Printer, RefreshCw, Search } from "lucide-react";
import { toPng } from "html-to-image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, ErrorState, LoadingState, Notice } from "@/components/common/States";
import { Checkbox } from "@/components/ui/checkbox";
import KartuID, { buatQr } from "@/components/attendance/KartuID";
import { fetcher } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";
import "./kartu-cetak.css";

// Kartu pratinjau digambar pada ukuran cetaknya (54 × 86 mm) lalu diperbesar dengan transform.
// Transform tidak mengubah ruang yang dihuni elemen, jadi kotak penampungnya dibuat sebesar
// hasil perbesaran — kalau tidak, sisa kartu meluber dan menimpa tombol di bawahnya.
const UKURAN_KARTU = { lebar: 54, tinggi: 86 };
const SKALA_PRATINJAU = 1.35;

/** Menyiapkan data cetak + mencetak lewat dokumen tersembunyi agar ukurannya persis 54 × 86 mm. */
const CetakKartuTab = () => {
  const [ta, setTa] = useState("semua");
  const [kelas, setKelas] = useState("semua");
  const [peran, setPeran] = useState("semua");
  const [cari, setCari] = useState("");
  const [pilih, setPilih] = useState({});
  const [pratinjau, setPratinjau] = useState(null);
  const [mode, setMode] = useState(null);          // "satuan" | "lembar"
  const [isi, setIsi] = useState(null);              // baris yang siap dicetak
  const [qr, setQr] = useState({});                  // kartu_id -> data URL
  const [unduhan, setUnduhan] = useState(null);
  const nodeKartu = useRef({});

  const params = new URLSearchParams();
  if (ta !== "semua") params.set("tahun_ajaran", ta);
  if (kelas !== "semua") params.set("kelas", kelas);
  if (peran !== "semua") params.set("peran", peran);
  const q = useQuery({ queryKey: ["kartu-cetak", ta, kelas, peran], queryFn: () => fetcher(`/school/attendance/kartu?${params}`) });

  const rows = useMemo(() => {
    const n = cari.trim().toLowerCase();
    return (q.data?.kartu || []).filter((k) => !n
      || (k.anggota?.nama || "").toLowerCase().includes(n)
      || (k.seri || "").toLowerCase().includes(n)
      || (k.anggota?.nisn || "").includes(n)
      || (k.anggota?.nip || "").includes(n));
  }, [q.data, cari]);

  // QR dibuat sekali untuk semua kartu yang tampil, supaya lembar cetak tidak kehilangan kode.
  useEffect(() => {
    let aktif = true;
    const daftar = (q.data?.kartu || []).filter((k) => !qr[k.kartu_id]);
    if (!daftar.length) return undefined;
    Promise.all(daftar.map(async (k) => [k.kartu_id, await buatQr(k.tautan)]))
      .then((pasang) => { if (aktif) setQr((prev) => ({ ...prev, ...Object.fromEntries(pasang) })); })
      .catch(() => toast.error("Kode QR gagal dibuat. Muat ulang halaman lalu coba lagi."));
    return () => { aktif = false; };
  }, [q.data, qr]);

  const terpilih = useMemo(() => rows.filter((k) => pilih[k.kartu_id]), [rows, pilih]);

  const siapkanCetak = useCallback(async (baris, gaya) => {
    if (!baris.length) { toast.warning("Pilih minimal satu kartu."); return; }
    const belum = baris.filter((k) => !qr[k.kartu_id]);
    if (belum.length) {
      const tambahan = Object.fromEntries(await Promise.all(
        belum.map(async (k) => [k.kartu_id, await buatQr(k.tautan)])));
      setQr((prev) => ({ ...prev, ...tambahan }));
    }
    setIsi(baris);
    setMode(gaya);
  }, [qr]);

  useEffect(() => {
    if (!mode || !isi) return undefined;
    const selesai = () => { window.print(); setMode(null); setIsi(null); };
    // Tunggu satu frame agar kartu (dan gambar QR) sempat terlayat sebelum dialog cetak muncul.
    const id = requestAnimationFrame(() => requestAnimationFrame(selesai));
    return () => cancelAnimationFrame(id);
  }, [mode, isi]);

  const unduhPng = async (k) => {
    const node = nodeKartu.current[k.kartu_id];
    if (!node) return;
    setUnduhan(k.kartu_id);
    try {
      const url = await toPng(node, { pixelRatio: 4, cacheBust: true, backgroundColor: "#FFFFFF" });
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(k.seri || k.kartu_id).replace(/[^\w-]/g, "_")}.png`;
      a.click();
      toast.success(`PNG ${k.anggota?.nama || "kartu"} diunduh.`);
    } catch {
      toast.error("Berkas PNG gagal dibuat. Gunakan Cetak lalu pilih Save as PDF.");
    } finally {
      setUnduhan(null);
    }
  };

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (!rows.length) {
    return <EmptyState title="Belum ada kartu yang bisa dicetak"
      description="Kartu ID dibuat dari kartu aktif yang sudah dipetakan ke anggota. Petakan kartu terlebih dahulu di tab Kartu RFID." />;
  }

  const p = { tahun_ajaran: [], kelas: [], peran: [], ...(q.data.pilihan || {}) };

  return (
    <div className="space-y-4" data-testid="kartu-cetak-tab">
      <div className="flex flex-wrap gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input className="pl-8" placeholder="Cari nama, seri, NISN/NIP…" value={cari}
            onChange={(e) => setCari(e.target.value)} data-testid="kartu-cetak-search" />
        </div>
        <Select value={ta} onValueChange={setTa}>
          <SelectTrigger className="w-full sm:w-44" data-testid="kartu-cetak-ta"><SelectValue placeholder="Tahun ajaran" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="semua">Semua tahun ajaran</SelectItem>
            {p.tahun_ajaran.map((v) => <SelectItem key={v} value={v}>T.A. {v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={kelas} onValueChange={setKelas}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Kelas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="semua">Semua kelas</SelectItem>
            {p.kelas.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={peran} onValueChange={setPeran}>
          <SelectTrigger className="w-full sm:w-44"><SelectValue placeholder="Peran" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="semua">Semua peran</SelectItem>
            {p.peran.map((v) => <SelectItem key={v} value={v}>{ROLE_LABELS[v] || v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-card px-4 py-3">
        <p className="text-sm font-semibold">
          {rows.length} kartu{terpilih.length ? ` · ${terpilih.length} dipilih` : ""}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setPilih(Object.fromEntries(rows.map((k) => [k.kartu_id, true])))}
            data-testid="kartu-cetak-pilih-semua">Pilih semua</Button>
          <Button variant="ghost" size="sm" onClick={() => setPilih({})}>Kosongkan</Button>
          <Button size="sm" onClick={() => siapkanCetak(terpilih.length ? terpilih : rows, "lembar")} data-testid="kartu-cetak-lembar">
            <Printer className="h-4 w-4 mr-1" /> Cetak lembar A4
          </Button>
        </div>
      </div>

      <Notice tone="info" testId="kartu-cetak-catatan">
        Kartu dicetak ukuran PVC 54 × 86 mm. Lembar A4 memuat 9 kartu per halaman dengan tanda potong.
        Untuk hasil warna penuh, aktifkan “Graphics/Background graphics” pada dialog cetak.
      </Notice>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {rows.map((k) => (
          <div key={k.kartu_id} className="space-y-2" data-testid={`kartu-cetak-${k.kartu_id}`}>
            <div ref={(el) => { nodeKartu.current[k.kartu_id] = el; }} className="flex justify-center">
              <KartuID kartu={k} sekolah={q.data.sekolah} qr={qr[k.kartu_id]} />
            </div>
            <div className="flex items-center gap-2 rounded-lg border bg-card px-2.5 py-2">
              <Checkbox id={`pilih-${k.kartu_id}`} checked={!!pilih[k.kartu_id]}
                onCheckedChange={(v) => setPilih((prev) => ({ ...prev, [k.kartu_id]: !!v }))} />
              <Label htmlFor={`pilih-${k.kartu_id}`} className="cursor-pointer text-xs font-semibold flex-1 truncate">
                {k.anggota?.nama}
              </Label>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPratinjau(k)} title="Perbesar">
                <Image className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => unduhPng(k)} disabled={unduhan === k.kartu_id}
                title="Unduh PNG" data-testid={`kartu-cetak-png-${k.kartu_id}`}>
                {unduhan === k.kartu_id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => siapkanCetak([k], "satuan")}
                title="Cetak satu kartu" data-testid={`kartu-cetak-satuan-${k.kartu_id}`}>
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!pratinjau} onOpenChange={(b) => { if (!b) setPratinjau(null); }}>
        <DialogContent className="max-w-lg max-h-[92dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pratinjau kartu</DialogTitle>
            <DialogDescription>Tampilan persis seperti yang akan tercetak, termasuk kode QR verifikasi.</DialogDescription>
          </DialogHeader>
          {pratinjau && (
            <div className="mx-auto shrink-0" style={{ width: `${UKURAN_KARTU.lebar * SKALA_PRATINJAU}mm`, height: `${UKURAN_KARTU.tinggi * SKALA_PRATINJAU}mm` }}
              data-testid="kartu-pratinjau-wadah">
              <div style={{ transform: `scale(${SKALA_PRATINJAU})`, transformOrigin: "top left" }}>
                <KartuID kartu={pratinjau} sekolah={q.data.sekolah} qr={qr[pratinjau.kartu_id]} />
              </div>
            </div>
          )}
          <div className="flex flex-wrap justify-center gap-2 pt-4">
            <Button variant="outline" onClick={() => unduhPng(pratinjau)} data-testid="kartu-pratinjau-png">Unduh PNG</Button>
            <Button onClick={() => { setPratinjau(null); siapkanCetak([pratinjau], "satuan"); }} data-testid="kartu-pratinjau-cetak">
              <Printer className="h-4 w-4 mr-1" /> Cetak kartu ini
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {mode && createPortal(
        <div className={`sist-cetak-root ${mode}`} data-testid="kartu-cetak-dokumen">
          {mode === "lembar"
            ? <LembarA4 baris={isi} sekolah={q.data.sekolah} qr={qr} />
            : <div className="sist-kartu-satuan"><KartuID kartu={isi[0]} sekolah={q.data.sekolah} qr={qr[isi[0].kartu_id]} /></div>}
        </div>,
        document.body
      )}
    </div>
  );
};

// 9 kartu per halaman A4 (3 × 3) dengan tanda potong di empat sudut tiap kartu.
const LembarA4 = ({ baris, sekolah, qr }) => (
  <div className="sist-lembar">
    {Array.from({ length: Math.ceil(baris.length / 9) }, (_, halaman) => (
      <div className="sist-lembar-halaman" key={halaman} data-testid={`kartu-lembar-${halaman}`}>
        {baris.slice(halaman * 9, halaman * 9 + 9).map((k) => (
          <div className="sist-lembar-sel" key={k.kartu_id}>
            <span className="pot pot-tl" /><span className="pot pot-tr" />
            <span className="pot pot-bl" /><span className="pot pot-br" />
            <KartuID kartu={k} sekolah={sekolah} qr={qr[k.kartu_id]} className="shadow-none ring-1 ring-black/20" />
          </div>
        ))}
      </div>
    ))}
  </div>
);

export default CetakKartuTab;
